import { useEffect } from "react";
import { Icon } from "../ui/Icon.js";
import { GameIcon } from "../assets/gameIcons.js";
import { useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { onWs } from "../api/ws.js";
import { toast } from "../ui/Toast.js";
import { ChatView } from "./ChatView.js";
import { MailView } from "./MailView.js";
import { BoardsView } from "./BoardsView.js";
import { NeighboursView } from "./NeighboursView.js";
import { SOCIAL_TABS, useSocial, type SocialTab } from "./state.js";
import type { ChatMessage } from "./types.js";
import "./social.css";

/** Subscribes the social store to pushes for the whole session (unread badges, live chat, mail). */
export function useSocialPushes(): void {
  useEffect(() => {
    const offMsg = onWs("chat.message", (e) => useSocial.getState().pushMessage(e.payload as ChatMessage));
    const offDel = onWs("chat.deleted", (e) => {
      const p = e.payload as { id: string; channel: string };
      useSocial.getState().markDeleted(p.id, p.channel);
    });
    const offMail = onWs("mail.new", (e) => {
      const p = e.payload as { subject: string; rewards: boolean };
      toast.info(<><Icon name="mail" /> {p.subject}{p.rewards ? <> <GameIcon id="itm.chest_gold" /></> : null}</>);
      void useSocial.getState().refreshMail();
    });
    const offMention = onWs("chat.mention", (e) => {
      const p = e.payload as { from: string; text: string };
      toast.info(`@ ${p.from}: ${p.text.slice(0, 60)}`);
    });
    const offMuted = onWs("chat.muted", () => void useSocial.getState().refreshChat());
    return () => {
      offMsg();
      offDel();
      offMail();
      offMention();
      offMuted();
    };
  }, []);
}

/** `/app/chat[/tab]`: Chat · Post · Ranglisten · Nachbarn in one hub (80-social.md). */
export function SocialHub({ route }: { route: string }) {
  const t = useT();
  const chat = useSocial((s) => s.chat);
  const mailUnread = useSocial((s) => s.mailUnread);
  const seg = route.split("/")[3] as SocialTab | undefined;
  const tab: SocialTab = seg && SOCIAL_TABS.includes(seg) ? seg : "chat";
  const badges: Record<SocialTab, number> = { chat: (chat?.unread.global ?? 0) + (chat?.unread.dm ?? 0), mail: mailUnread, boards: 0, neighbours: 0 };
  return (
    <div className="page social" data-testid="social-hub">
      <div className="row tabs" style={{ padding: "0 12px 8px" }}>
        {SOCIAL_TABS.map((x) => (
          <button key={x} type="button" className={`tab ${x === tab ? "active" : ""}`} onClick={() => navigate(x === "chat" ? "/app/chat" : `/app/chat/${x}`)} data-testid={`social-tab-${x}`}>
            {t(`social.tab.${x}`)}
            {badges[x] > 0 ? <span className="badge warn" style={{ marginLeft: 6 }}>{badges[x]}</span> : null}
          </button>
        ))}
      </div>
      {tab === "chat" ? <ChatView /> : null}
      {tab === "mail" ? <MailView /> : null}
      {tab === "boards" ? <BoardsView /> : null}
      {tab === "neighbours" ? <NeighboursView /> : null}
    </div>
  );
}
